import {
  AppLayout,
  HeaderIconEye,
  HeaderIconPerson,
  useTheme,
  useTranslation,
  type HeaderAction,
  type MenuItem,
  type ViewAsOption,
} from '@e-learning/shared';
import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// Importar assets diretamente
import iconCourses from '../../assets/icon-courses.png';
import iconDashboard from '../../assets/icon-dashboard.png';
import iconGamification from '../../assets/icon-gamefication.png';
import iconGroup from '../../assets/icon-group.png';
import iconLeaderboard from '../../assets/icon-leaderborder.png';
import iconQuestionBank from '../../assets/icon-question-bank.png';
import iconUsers from '../../assets/icon-users.png';
import logoFeraMenuSide from '../../assets/logo-fera-menu-side.png';

const iconSize = 20;
const inactiveIconFilter =
  'brightness(0) saturate(100%) invert(90%) sepia(8%) saturate(271%) hue-rotate(180deg) brightness(96%) contrast(91%)';
const activeIconFilter = 'brightness(0) invert(1)';

const MenuIcon = ({
  src,
  alt,
  active = false,
}: {
  src: string;
  alt: string;
  active?: boolean;
}) => (
  <img
    src={src}
    alt={alt}
    width={iconSize}
    height={iconSize}
    style={{
      objectFit: 'contain',
      display: 'block',
      minWidth: `${iconSize}px`,
      minHeight: `${iconSize}px`,
      flexShrink: 0,
      // Os ícones do menu são PNG; aplicamos filtro para acompanhar as cores do estado do item.
      filter: active ? activeIconFilter : inactiveIconFilter,
    }}
    aria-hidden
  />
);

interface MenuConfigItem {
  id: string;
  labelKey: string;
  iconSrc: string;
  to: string;
}

const adminMenuConfig: MenuConfigItem[] = [
  {
    id: 'dashboard',
    labelKey: 'menu.dashboard',
    iconSrc: iconDashboard,
    to: '/admin',
  },
  {
    id: 'users',
    labelKey: 'menu.users',
    iconSrc: iconUsers,
    to: '/admin/users',
  },
  {
    id: 'groups',
    labelKey: 'menu.groups',
    iconSrc: iconGroup,
    to: '/admin/groups',
  },
  {
    id: 'courses',
    labelKey: 'menu.courses',
    iconSrc: iconCourses,
    to: '/admin/courses',
  },
  {
    id: 'question-bank',
    labelKey: 'menu.question_bank',
    iconSrc: iconQuestionBank,
    to: '/admin/question-bank',
  },
  {
    id: 'gamification',
    labelKey: 'menu.gamification',
    iconSrc: iconGamification,
    to: '/admin/gamification',
  },
  {
    id: 'leaderboard',
    labelKey: 'menu.leaderboard',
    iconSrc: iconLeaderboard,
    to: '/admin/leaderboard',
  },
];

function normalizePathname(raw: string): string {
  const pathname =
    raw === '' || raw === '/' ? '/' : raw.replace(/\/$/, '') || '/';
  return pathname;
}

function useAdminMenuItems(): MenuItem[] {
  const location = useLocation();
  const { t } = useTranslation();
  const pathname = normalizePathname(location.pathname || '');

  const items = adminMenuConfig.map(item => {
    const label = t(item.labelKey);
    const itemPath = item.to ?? '/';
    const itemPathNorm = normalizePathname(itemPath);
    const isAdminRoot = itemPathNorm === '/admin';
    const active = isAdminRoot
      ? pathname === itemPathNorm
      : pathname === itemPathNorm ||
        (itemPathNorm !== '/' && pathname.startsWith(itemPathNorm + '/'));

    return {
      href: item.to ?? '/',
      id: item.id,
      label,
      active,
      icon: <MenuIcon src={item.iconSrc} alt={label} active={active} />,
    };
  });

  const hasActive = items.some(item => item.active);
  if (!hasActive && items.length > 0) {
    items[0].active = true;
  }
  return items;
}

function renderMenuItemWithRouter(
  item: MenuItem,
  content: React.ReactNode,
  itemStyle: React.CSSProperties
) {
  const to = item.to ?? item.href;
  return (
    <Link to={to} style={itemStyle}>
      {content}
    </Link>
  );
}

const ChevronDown = () => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

function StudentHeaderSearch() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get('search') ?? '';
  const [value, setValue] = useState(urlSearch);
  const inputClass = 'aluno-shell-header-search-input';

  useEffect(() => {
    setValue(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const normalized = value.trim();
      const current = searchParams.get('search') ?? '';
      if (normalized === current) return;
      const next = new URLSearchParams(searchParams);
      if (normalized) {
        next.set('search', normalized);
      } else {
        next.delete('search');
      }
      setSearchParams(next, { replace: true });
    }, 300);

    return () => {
      window.clearTimeout(handle);
    };
  }, [value, searchParams, setSearchParams]);

  const wrapperStyle: CSSProperties = {
    position: 'relative',
    width: 'min(100%, 480px)',
    minWidth: 120,
    flexShrink: 0,
  };

  const iconWrap: CSSProperties = {
    position: 'absolute',
    left: theme.spacing.md,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(255, 255, 255, 0.55)',
    display: 'flex',
    pointerEvents: 'none',
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    height: 40,
    paddingLeft: '2.35rem',
    paddingRight: theme.spacing.md,
    fontSize: theme.typography.fontSize.sm,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.normal,
    color: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    borderRadius: theme.shape.borderRadius.md,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={wrapperStyle} role="search">
      <style>{`.${inputClass}::placeholder { color: rgba(255, 255, 255, 0.45); }`}</style>
      <span style={iconWrap}>
        <SearchIcon />
      </span>
      <input
        type="search"
        className={inputClass}
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={t('student_shell.header_search_placeholder')}
        aria-label={t('student_shell.header_search_aria')}
        style={inputStyle}
        data-e2e="aluno-shell-header-search"
      />
    </div>
  );
}

export interface AppShellProps {
  /** Admin: título no header vem do item ativo do menu. Student: barra de pesquisa no lugar do título. */
  layoutVariant?: 'admin' | 'student';
  /** Título exibido no cabeçalho (ex.: "User Management") */
  headerTitle?: string;
  /** Nome do usuário no header */
  userName?: string;
  /** Email do usuário no header */
  userEmail?: string;
  /** Seletor "View As": label e opções. Passar false para ocultar. Omitir para exibir com padrão (Admin / Student). */
  viewAs?:
    | {
        label?: string;
        options?: ViewAsOption[];
        defaultValue?: string;
      }
    | false;
  /** Chamado ao clicar em "Sair" no menu do usuário. Deve limpar tokens e redirecionar para o login. */
  onLogout?: () => void;
  children: ReactNode;
}

const defaultViewAsOptions: ViewAsOption[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'student', label: 'Student' },
];

export function AppShell({
  layoutVariant = 'admin',
  userName = '',
  userEmail = '',
  viewAs,
  onLogout,
  children,
}: AppShellProps) {
  const navigate = useNavigate();
  const adminMenuItems = useAdminMenuItems();
  const isStudentLayout = layoutVariant === 'student';
  const menuItems = isStudentLayout ? [] : adminMenuItems;
  const activeItem = adminMenuItems.find(item => item.active);
  const displayTitle = activeItem ? activeItem.label : '';
  const displayTitleIcon = activeItem ? activeItem.icon : undefined;

  const viewAsConfig =
    viewAs && typeof viewAs === 'object' ? viewAs : undefined;
  const viewAsOptions = viewAsConfig?.options ?? defaultViewAsOptions;
  const viewAsValue = isStudentLayout ? 'student' : 'admin';

  const handleViewAsChange = (value: string) => {
    if (!isStudentLayout && value === 'student') {
      navigate('/student', { replace: true });
    }
    if (isStudentLayout && value === 'admin') {
      navigate('/admin', { replace: true });
    }
  };

  const headerActions: HeaderAction[] = [];
  headerActions.push({
    key: 'user',
    label: (
      <>
        <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
          <div style={{ fontWeight: 500 }}>{userName}</div>
          <div style={{ fontSize: 12, opacity: 0.9 }}>{userEmail}</div>
        </div>
        <ChevronDown />
      </>
    ),
    icon: <HeaderIconPerson />,
    menuItems: [
      { label: 'Perfil', onClick: () => {} },
      {
        label: 'Sair',
        onClick: () => {
          onLogout?.();
        },
      },
    ],
  });

  return (
    <AppLayout
      hideSidebar={isStudentLayout}
      logo={
        <img
          src={logoFeraMenuSide}
          alt="FERA"
          style={{ maxHeight: 40, width: 'auto' }}
        />
      }
      menuItems={menuItems}
      renderMenuItem={isStudentLayout ? undefined : renderMenuItemWithRouter}
      headerTitle={isStudentLayout ? '' : displayTitle}
      headerTitleIcon={isStudentLayout ? undefined : displayTitleIcon}
      headerTitleContent={isStudentLayout ? <StudentHeaderSearch /> : undefined}
      headerViewAs={
        viewAs !== false
          ? {
              label: '',
              icon: <HeaderIconEye />,
              options: viewAsOptions,
              value: viewAsValue,
              onChange: handleViewAsChange,
            }
          : undefined
      }
      headerActions={headerActions}
      copyright="© 2026 FERA Diagnostics"
    >
      {children}
    </AppLayout>
  );
}
